import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { bookingsAPI, roomsAPI, propertiesAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useProperty } from '../../contexts/PropertyContext';
import { NewBookingModal } from '../../components/NewBookingModal';
import { getCurrencySymbol } from '../../lib/currencies';
import { format, addDays, subDays } from 'date-fns';
import { Button } from '../../components/ui/button';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Undo2,
  CalendarDays,
  Users,
  MoreVertical,
  LogIn,
  LogOut,
  History,
  Clock,
  XCircle,
  PlusCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { BookingActionModal, BOOKING_ACTIONS } from '../../components/BookingActionModal';
import { SalesAnalyticsCard, RecentReservationsCard } from '../../components/DashboardWidgets';

/* ──────────────────────── Action Menu ──────────────────────── */
function BookingMenu({ booking, type, onAction, onOpenActions }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) { document.addEventListener('mousedown', close); return () => document.removeEventListener('mousedown', close); }
  }, [open]);

  const isCheckedIn = booking.checked_in;
  const isCheckedOut = booking.checked_out;

  const quickItems = [];
  if (type === 'arrivals' && !isCheckedIn && booking.status !== 'no_show' && booking.status !== 'cancelled') {
    quickItems.push({ label: 'Check In', icon: LogIn, action: () => onOpenActions(booking, 'guest_info'), color: 'text-emerald-600' });
    quickItems.push({ label: 'No Show', icon: Clock, action: () => onAction(booking.id, 'no-show'), color: 'text-orange-600' });
    quickItems.push({ label: 'Cancel', icon: XCircle, action: () => onAction(booking.id, 'cancel'), color: 'text-red-600' });
  }
  if (type === 'arrivals' && isCheckedIn && !isCheckedOut) {
    quickItems.push({ label: 'Undo Check In', icon: Undo2, action: () => onAction(booking.id, 'undo-checkin'), color: 'text-amber-600' });
  }
  if (type === 'departures' && !isCheckedOut) {
    quickItems.push({ label: 'Check Out', icon: LogOut, action: () => onOpenActions(booking, 'checkout'), color: 'text-red-600' });
  }
  if (!isCheckedIn && !isCheckedOut && booking.status !== 'no_show' && booking.status !== 'cancelled' && type !== 'arrivals') {
    quickItems.push({ label: 'Cancel', icon: XCircle, action: () => onAction(booking.id, 'cancel'), color: 'text-red-600' });
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid={`booking-menu-${booking.id}`}>
        <MoreVertical className="h-5 w-5 text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 z-50 w-[280px] py-2 overflow-hidden" data-testid={`booking-menu-dropdown-${booking.id}`}>
          {quickItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <button key={i} onClick={() => { item.action(); setOpen(false); }} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium hover:bg-gray-50 ${item.color}`} data-testid={`quick-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="h-4 w-4" />{item.label}
              </button>
            );
          })}
          {quickItems.length > 0 && <div className="border-t border-gray-100 my-1" />}
          <div className="grid grid-cols-2 gap-0">
            {BOOKING_ACTIONS.map(act => {
              const Icon = act.icon;
              return (
                <button key={act.id} onClick={() => { onOpenActions(booking, act.id); setOpen(false); }} className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors" data-testid={`action-${act.id}-${booking.id}`}>
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center ${act.bg}`}><Icon className={`h-3.5 w-3.5 ${act.color}`} /></div>
                  <span className="truncate">{act.label}</span>
                </button>
              );
            })}
          </div>
          <div className="border-t border-gray-100 my-1" />
          <button onClick={() => { onOpenActions(booking, 'history'); setOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50" data-testid={`view-history-${booking.id}`}>
            <History className="h-4 w-4 text-gray-400" />View History
          </button>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Guest Card ──────────────────────── */
function GuestCard({ booking, type, onAction, actionLoading, onOpenActions }) {
  const [expanded, setExpanded] = useState(false);
  const isCheckedIn = booking.checked_in;
  const isCheckedOut = booking.checked_out;
  const currency = booking.currency || 'USD';
  const sym = getCurrencySymbol(currency);
  const source = booking.source || 'direct';
  const sourceLabel = booking.ota_name || (source === 'channex' ? 'Channex' : source === 'walk_in' ? 'Walk-in' : 'Direct');

  const getSourceBadge = () => {
    const first = sourceLabel.charAt(0).toUpperCase();
    const colors = { 'B': 'bg-blue-600', 'C': 'bg-orange-500', 'D': 'bg-emerald-600', 'W': 'bg-purple-600', 'E': 'bg-rose-500' };
    return (
      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold ${colors[first] || 'bg-gray-500'}`}>
        {first}
      </span>
    );
  };

  const actionSection = () => {
    if (type === 'arrivals') {
      if (isCheckedOut) {
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end gap-1">
              <span className="meta-text">CHECKED OUT {booking.checked_out_at ? format(new Date(booking.checked_out_at), 'HH:mm') : ''}</span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-gray-100 text-gray-400 text-[12px] font-semibold tracking-wide"><LogOut className="h-3.5 w-3.5" /> CHECKED OUT</span>
            </div>
            <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
          </div>
        );
      }
      if (isCheckedIn) {
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end gap-1">
              <span className="meta-text">CHECKED IN {booking.checked_in_at ? format(new Date(booking.checked_in_at), 'HH:mm') : ''}</span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-emerald-50 text-emerald-600 text-[12px] font-semibold tracking-wide"><LogIn className="h-3.5 w-3.5" /> CHECKED IN</span>
            </div>
            <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
          </div>
        );
      }
      if (booking.status === 'no_show') {
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end gap-1">
              <span className="meta-text">MARKED</span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-orange-50 text-orange-600 text-[12px] font-semibold tracking-wide"><Clock className="h-3.5 w-3.5" /> NO SHOW</span>
            </div>
            <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
          </div>
        );
      }
      if (booking.status === 'cancelled') {
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end gap-1">
              <span className="meta-text">CANCELLED</span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-red-50 text-red-500 text-[12px] font-semibold tracking-wide"><XCircle className="h-3.5 w-3.5" /> CANCELLED</span>
            </div>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-end gap-1.5">
            {booking.payment_status === 'paid' ? (
              <div className="text-xs text-emerald-500 font-semibold flex items-center gap-1">
                <span className="text-emerald-500">&#10003;</span> CARD IS STORED
              </div>
            ) : (
              <div className="text-xs text-red-500 font-semibold flex items-center gap-1">
                <span className="text-red-500">&#10005;</span> NO CARD STORED
              </div>
            )}
            <button
              onClick={() => onOpenActions(booking, 'check_in')}
              disabled={actionLoading === booking.id}
              className="btn-success"
              data-testid={`checkin-btn-${booking.id}`}
            >
              {actionLoading === booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-3.5 w-3.5" /> CHECK IN</>}
            </button>
          </div>
          <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
        </div>
      );
    }
    if (type === 'departures') {
      if (isCheckedOut) {
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end gap-1">
              <span className="meta-text">CHECKED OUT {booking.checked_out_at ? format(new Date(booking.checked_out_at), 'HH:mm') : ''}</span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-gray-100 text-gray-400 text-[12px] font-semibold tracking-wide"><LogOut className="h-3.5 w-3.5" /> CHECKED OUT</span>
            </div>
            <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
          </div>
        );
      }
      const totalPaid = booking.amount_paid || 0;
      const totalAmount = booking.total_amount || 0;
      const hasBalance = totalAmount > 0 && totalPaid < totalAmount;
      return (
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-end gap-1.5">
            <button
              onClick={() => onAction(booking.id, 'checkout')}
              disabled={actionLoading === booking.id || hasBalance}
              className={hasBalance ? 'btn-outline opacity-50 cursor-not-allowed' : 'btn-danger'}
              data-testid={`checkout-btn-${booking.id}`}
            >
              {actionLoading === booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-3.5 w-3.5" /> CHECK OUT</>}
            </button>
            {hasBalance && (
              <Link to="/admin/ledger" className="text-[11px] text-red-500 hover:underline font-medium" data-testid={`settle-link-${booking.id}`}>
                Balance due — settle in Payments
              </Link>
            )}
          </div>
          <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
        </div>
      );
    }
    // Stayovers
    return (
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end gap-1">
          <span className="meta-text">IN HOUSE</span>
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-blue-50 text-blue-600 text-[12px] font-semibold tracking-wide"><LogIn className="h-3.5 w-3.5" /> STAYING</span>
        </div>
        <BookingMenu booking={booking} type={type} onAction={onAction} onOpenActions={onOpenActions} />
      </div>
    );
  };

  return (
    <div
      className={`border-b last:border-b-0 px-5 py-4 transition-all ${isCheckedIn && type === 'arrivals' && !isCheckedOut ? 'opacity-50' : ''}`}
      data-testid={`guest-card-${booking.id}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        {/* Guest info */}
        <div className="flex-shrink-0 lg:w-44">
          <p className="text-[15px] font-semibold leading-tight text-gray-900">{booking.guest_name || 'Unknown Guest'}</p>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {booking.room_type_name}
            {booking.room_number && <span className="ml-1.5 text-[11px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">Rm {booking.room_number}</span>}
          </p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] font-semibold tracking-wide uppercase mt-2 text-violet-500 hover:text-violet-600 flex items-center gap-1"
            data-testid={`notes-toggle-${booking.id}`}
          >
            DETAILS {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>

        {/* Date calendar blocks */}
        <div className="flex-shrink-0 flex items-stretch rounded-lg overflow-hidden border border-gray-200" data-testid={`date-block-${booking.id}`}>
          <div className="flex flex-col">
            <div className="bg-gray-800 text-white text-[11px] font-semibold px-6 py-1 flex items-center justify-center gap-1.5">
              <CalendarDays className="h-3 w-3" /> {booking.nights || Math.max(1, Math.round((new Date(booking.check_out + 'T00:00:00') - new Date(booking.check_in + 'T00:00:00')) / 86400000))} night{(booking.nights || Math.max(1, Math.round((new Date(booking.check_out + 'T00:00:00') - new Date(booking.check_in + 'T00:00:00')) / 86400000))) !== 1 ? 's' : ''}
            </div>
            <div className="flex">
              <div className="text-center px-4 py-2 bg-white border-r border-gray-200">
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Arriving {format(new Date(booking.check_in + 'T00:00:00'), 'EEE')}</p>
                <p className="text-2xl font-extrabold text-red-500 leading-none mt-0.5">{format(new Date(booking.check_in + 'T00:00:00'), 'd')}</p>
                <p className="text-[11px] font-bold text-gray-700 uppercase mt-0.5">{format(new Date(booking.check_in + 'T00:00:00'), 'MMM yy')}</p>
              </div>
              <div className="text-center px-4 py-2 bg-white">
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Departing {format(new Date(booking.check_out + 'T00:00:00'), 'EEE')}</p>
                <p className="text-2xl font-extrabold text-red-500 leading-none mt-0.5">{format(new Date(booking.check_out + 'T00:00:00'), 'd')}</p>
                <p className="text-[11px] font-bold text-gray-700 uppercase mt-0.5">{format(new Date(booking.check_out + 'T00:00:00'), 'MMM yy')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Booking details grid */}
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
          {[
            { label: 'Rate Type', value: booking.rate_type || 'Room Only' },
            { label: 'Occupancy', value: `${booking.guest_count || 1} Guest${(booking.guest_count || 1) > 1 ? 's' : ''}` },
            { label: 'Arrival', value: booking.arrival_time || '—' },
            { label: 'Nights', value: booking.nights },
            { label: 'Balance', value: `${sym}${(booking.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { label: 'Source', value: sourceLabel },
          ].map(item => (
            <div key={item.label}>
              <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">{item.label}</p>
              <p className="text-[13px] font-medium text-gray-700 mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Action */}
        <div className="flex-shrink-0">
          {actionSection()}
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-dashed grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { label: 'Ref', value: booking.booking_reference },
            { label: 'Email', value: booking.guest_email || '—' },
            { label: 'Phone', value: booking.guest_phone || '—' },
            { label: 'Property', value: booking.property_name },
            { label: 'Booked', value: booking.created_at ? format(new Date(booking.created_at), 'd MMM yyyy') : '—' },
          ].map(item => (
            <p key={item.label} className="text-[12px] text-gray-500"><span className="font-medium text-gray-700">{item.label}:</span> {item.value}</p>
          ))}
          {booking.notes && <p className="col-span-2 text-[12px] text-gray-500"><span className="font-medium text-gray-700">Notes:</span> {booking.notes}</p>}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Check-In Modal ──────────────────────── */
/* ──────────────────────── Dashboard Content ──────────────────────── */
export default function AdminDashboard() {
  return (
    <AdminLayout>
      <DashboardContent />
    </AdminLayout>
  );
}

function DashboardContent() {
  const { selectedProperty } = useProperty();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('arrivals');
  const [actionLoading, setActionLoading] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [allProperties, setAllProperties] = useState([]);
  const [newBookingOpen, setNewBookingOpen] = useState(false);

  const fetchDaily = useCallback(async (date) => {
    setDailyLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await bookingsAPI.daily(dateStr, selectedProperty || undefined);
      setDailyData(res.data);
    } catch {
      toast.error('Failed to load daily data');
    } finally {
      setDailyLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => { fetchDaily(selectedDate); }, [selectedDate, fetchDaily]);

  useEffect(() => {
    (async () => {
      try { const r = await propertiesAPI.list(); setAllProperties(r.data || []); } catch { /* ignore */ }
    })();
  }, []);

  const handleAction = async (bookingId, action) => {
    setActionLoading(bookingId);
    try {
      if (action === 'undo-checkin') {
        await bookingsAPI.undoCheckin(bookingId);
        toast.success('Check-in undone');
      } else if (action === 'checkout') {
        await bookingsAPI.checkout(bookingId);
        toast.success('Guest checked out');
      } else if (action === 'no-show') {
        await bookingsAPI.noShow(bookingId);
        toast.success('Booking marked as no show');
      } else if (action === 'cancel') {
        await bookingsAPI.cancel(bookingId);
        toast.success('Booking cancelled');
      }
      await fetchDaily(selectedDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAmend = async (bookingId, data) => {
    await bookingsAPI.amend(bookingId, data);
    await fetchDaily(selectedDate);
  };

  const goToday = () => setSelectedDate(new Date());
  const goPrev = () => setSelectedDate((d) => subDays(d, 1));
  const goNext = () => setSelectedDate((d) => addDays(d, 1));
  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const currentList = activeTab === 'arrivals'
    ? dailyData?.arrivals || []
    : activeTab === 'departures'
    ? dailyData?.departures || []
    : dailyData?.stayovers || [];

  const dateDisplay = format(selectedDate, 'EEEE, d MMMM yyyy');

  return (
      <div className="page-wrapper" data-testid="admin-dashboard">
        {/* Page Header */}
        <div className="page-header flex items-center justify-between">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Daily operations, check-ins and performance overview.</p>
          </div>
          <Button onClick={() => setNewBookingOpen(true)} data-testid="new-booking-btn">
            <PlusCircle className="h-4 w-4 mr-1.5" /> New Booking
          </Button>
        </div>

        {/* Dashboard Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SalesAnalyticsCard />
          <RecentReservationsCard onBookingClick={(b) => setActionModal({ booking: b, tab: 'guest_info' })} />
        </div>

        {/* Date Navigation */}
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button
              onClick={goToday}
              className={`px-3 py-1.5 rounded-lg border text-[13px] font-semibold transition-colors ${
                isToday ? 'bg-violet-50 border-violet-200 text-violet-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
              data-testid="today-btn"
            >
              TODAY
            </button>
            <div className="flex items-center">
              <button onClick={goPrev} className="p-1.5 hover:bg-white rounded-lg transition-colors" data-testid="prev-day-btn">
                <ChevronLeft className="h-4 w-4 text-gray-400" />
              </button>
              <button onClick={goNext} className="p-1.5 hover:bg-white rounded-lg transition-colors" data-testid="next-day-btn">
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            <h1 className="page-title flex items-center gap-2" data-testid="selected-date">
              {dateDisplay}
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-gray-200" data-testid="daily-tabs">
          {[
            { key: 'arrivals', label: 'Arrivals', count: `${dailyData?.arrivals_done || 0}/${dailyData?.arrivals_total || 0}`, sub: 'Done / Total', accent: 'text-emerald-500' },
            { key: 'stayovers', label: 'Stayovers', count: `${dailyData?.stayovers_total || 0}`, sub: 'Total', accent: 'text-blue-500' },
            { key: 'departures', label: 'Departures', count: `${dailyData?.departures_done || 0}/${dailyData?.departures_total || 0}`, sub: 'Done / Total', accent: 'text-amber-500' },
          ].map(tab => (
            <button
              key={tab.key}
              className={`flex-1 sm:flex-none sm:min-w-[160px] px-5 py-3 text-left border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-violet-600 bg-white'
                  : 'border-transparent hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
            >
              <p className={`text-[13px] font-bold tracking-wide uppercase ${activeTab === tab.key ? 'text-gray-900' : 'text-gray-400'}`}>
                {tab.label}
              </p>
              <p className={`text-xs mt-0.5 ${activeTab === tab.key ? 'text-gray-500' : 'text-gray-400'}`}>
                <span className={`${tab.accent} font-semibold`}>{tab.count}</span>
                <span className="ml-1">{tab.sub}</span>
              </p>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white border border-gray-200 border-t-0 rounded-b-lg">
          {dailyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : currentList.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No {activeTab} for {format(selectedDate, 'd MMMM yyyy')}</p>
            </div>
          ) : (
            <div>
              {currentList.map((booking) => (
                <GuestCard
                  key={booking.id}
                  booking={booking}
                  type={activeTab}
                  onAction={handleAction}
                  actionLoading={actionLoading}
                  onOpenActions={(b, tab) => setActionModal({ booking: b, tab })}
                />
              ))}
            </div>
          )}
        </div>

        {/* Unified Booking Action Modal */}
        {actionModal && (
          <BookingActionModal
            booking={actionModal.booking}
            initialTab={actionModal.tab}
            properties={allProperties}
            propertyTimezone={(() => { const p = allProperties.find(x => x.id === actionModal.booking.property_id); return p?.timezone; })()}
            onAmend={handleAmend}
            onCheckout={(id) => handleAction(id, 'checkout')}
            onTransfer={(id, data) => bookingsAPI.transfer(id, data)}
            onClose={() => setActionModal(null)}
            onRefresh={() => fetchDaily(selectedDate)}
          />
        )}

        <NewBookingModal
          open={newBookingOpen}
          onOpenChange={setNewBookingOpen}
          properties={allProperties}
          selectedProperty={selectedProperty}
          onCreated={() => fetchDaily(selectedDate)}
        />
      </div>
  );
}
